import { erf } from './erf';
import { sqrt } from './sqrt';

import {
  OptionContractChain,
  OptionContractChainDetails,
} from '@libs/api-reference';
import { POSITIONS_SIDE } from '../../constants';

type TCalcBreakPointArgs = Pick<
  OptionContractChainDetails,
  'strikePrice' | 'lastTradePrice'
>;

type TCalcBreakPointPercentArgs = Pick<
  OptionContractChain,
  'underlyingAssetLastTradePrice'
> & { breakPoint: number };

type TCalcLeverageArgs = Pick<
  OptionContractChain,
  'underlyingAssetLastTradePrice'
> &
  Pick<OptionContractChainDetails, 'lastTradePrice'> & { delta: number };

type TCalcSymbolStatusArgs = Pick<
  OptionContractChain,
  'underlyingAssetLastTradePrice' | 'cashSettlementFee'
> &
  Pick<OptionContractChainDetails, 'strikePrice'>;

type TCalcSymbolPricesArgs = Pick<
  OptionContractChain,
  | 'blackScholesRateOfReturn'
  | 'blackScholesVolatility'
  | 'underlyingAssetLastTradePrice'
  | 'remainingDays'
> &
  Pick<OptionContractChainDetails, 'strikePrice'>;

type OptionContractChainDetailsWithPrices = OptionContractChainDetails & {
  breakPoint: number;
  breakPointPercentage: number;
  status: number;
  blackScholes: number;
  delta: number;
  leverage: number;
};

type TCalcSymbolPrices = Record<
  string,
  {
    call: OptionContractChainDetailsWithPrices;
    put: OptionContractChainDetailsWithPrices;
    strikePrice: number;
  }
>;

function normalCDF(x: number, mean = 0, std = 1) {
  return 0.5 * (1 + erf((x - mean) / (std * sqrt(2))));
}

function calcSymbolCallBreakPointPrice({
  lastTradePrice,
  strikePrice,
}: TCalcBreakPointArgs): number {
  return strikePrice + lastTradePrice;
}

function calcSymbolPutBreakPointPrice({
  lastTradePrice,
  strikePrice,
}: TCalcBreakPointArgs): number {
  return strikePrice - lastTradePrice;
}

function calcSymbolBreakPointPercent({
  breakPoint,
  underlyingAssetLastTradePrice,
}: TCalcBreakPointPercentArgs): number {
  const result = breakPoint / underlyingAssetLastTradePrice;
  return (result - 1) * 100;
}

function calcSymbolCallLeverage({
  delta,
  underlyingAssetLastTradePrice,
  lastTradePrice,
}: TCalcLeverageArgs): number {
  return (underlyingAssetLastTradePrice / lastTradePrice) * delta;
}

function calcSymbolPutLeverage({
  delta,
  underlyingAssetLastTradePrice,
  lastTradePrice,
}: TCalcLeverageArgs): number {
  return Math.abs((underlyingAssetLastTradePrice / lastTradePrice) * delta);
}

function calcSymbolCallStatus({
  strikePrice,
  underlyingAssetLastTradePrice,
  cashSettlementFee,
}: TCalcSymbolStatusArgs): number {
  const settlement = 1 - cashSettlementFee;
  return underlyingAssetLastTradePrice - settlement * strikePrice;
}

function calcSymbolPutStatus({
  strikePrice,
  underlyingAssetLastTradePrice,
  cashSettlementFee,
}: TCalcSymbolStatusArgs): number {
  const settlement = 1 - cashSettlementFee;
  return settlement * strikePrice - underlyingAssetLastTradePrice;
}

type calcSymbolCallPricesReturnType = {
  delta: number;
  blackScholes: number;
};

function calcSymbolCallPrices({
  strikePrice,
  underlyingAssetLastTradePrice,
  remainingDays: days,
  blackScholesVolatility,
  blackScholesRateOfReturn,
}: TCalcSymbolPricesArgs): calcSymbolCallPricesReturnType {
  const remainingDays = days / 365;

  const d1 =
    (Math.log(underlyingAssetLastTradePrice / strikePrice) +
      (blackScholesRateOfReturn + 0.5 * blackScholesVolatility ** 2) *
        remainingDays) /
    (blackScholesVolatility * Math.sqrt(remainingDays));
  const d2 = d1 - blackScholesVolatility * Math.sqrt(remainingDays);

  const blackScholes =
    underlyingAssetLastTradePrice * normalCDF(d1) -
    strikePrice *
      Math.exp(-blackScholesRateOfReturn * remainingDays) *
      normalCDF(d2);

  return {
    blackScholes,
    delta: normalCDF(d1),
  };
}

function calcSymbolPutPrices({
  strikePrice,
  underlyingAssetLastTradePrice,
  blackScholesRateOfReturn,
  blackScholesVolatility,
  remainingDays: days,
}: TCalcSymbolPricesArgs): calcSymbolCallPricesReturnType {
  const remainingDays = days / 365;

  const d1 =
    (Math.log(underlyingAssetLastTradePrice / strikePrice) +
      (blackScholesRateOfReturn + 0.5 * blackScholesVolatility ** 2) *
        remainingDays) /
    (blackScholesVolatility * Math.sqrt(remainingDays));
  const d2 = d1 - blackScholesVolatility * Math.sqrt(remainingDays);

  const blackScholes =
    strikePrice *
      Math.exp(-blackScholesRateOfReturn * remainingDays) *
      normalCDF(-d2) -
    underlyingAssetLastTradePrice * normalCDF(-d1);

  return {
    blackScholes,
    delta: normalCDF(d1) - 1,
  };
}

class CalcSymbolValues {
  #instance: OptionContractChain;

  constructor(symbol: OptionContractChain, private readonly _contractType = 0) {
    this.#instance = symbol;
  }

  private calcPerSymbol(
    symbol: OptionContractChainDetails
  ): OptionContractChainDetailsWithPrices {
    const {
      remainingDays = 0,
      cashSettlementFee = 0,
      blackScholesVolatility = 0,
      blackScholesRateOfReturn = 0,
      underlyingAssetLastTradePrice = 0,
    } = this.#instance;

    const { contractType, strikePrice, lastTradePrice = 0 } = symbol;

    const isBuyContract = contractType === POSITIONS_SIDE.buy;

    let status = 0;
    let leverage = 0;
    let breakPoint = 0;
    let breakPointPercentage = 0;

    if (isBuyContract) {
      breakPoint = calcSymbolCallBreakPointPrice({
        strikePrice,
        lastTradePrice,
      });

      breakPointPercentage = calcSymbolBreakPointPercent({
        breakPoint,
        underlyingAssetLastTradePrice,
      });

      status = calcSymbolCallStatus({
        strikePrice,
        cashSettlementFee,
        underlyingAssetLastTradePrice,
      });

      const { blackScholes, delta } = calcSymbolCallPrices({
        strikePrice,
        remainingDays,
        blackScholesVolatility,
        blackScholesRateOfReturn,
        underlyingAssetLastTradePrice,
      });

      leverage = calcSymbolCallLeverage({
        delta,
        lastTradePrice,
        underlyingAssetLastTradePrice,
      });

      return {
        ...symbol,
        breakPoint,
        breakPointPercentage,
        status,
        blackScholes,
        delta,
        leverage,
      };
    }

    breakPoint = calcSymbolPutBreakPointPrice({
      strikePrice,
      lastTradePrice,
    });

    breakPointPercentage = calcSymbolBreakPointPercent({
      breakPoint,
      underlyingAssetLastTradePrice,
    });

    status = calcSymbolPutStatus({
      strikePrice,
      cashSettlementFee,
      underlyingAssetLastTradePrice,
    });

    const { blackScholes, delta } = calcSymbolPutPrices({
      strikePrice,
      remainingDays,
      blackScholesVolatility,
      blackScholesRateOfReturn,
      underlyingAssetLastTradePrice,
    });

    leverage = calcSymbolPutLeverage({
      delta,
      lastTradePrice,
      underlyingAssetLastTradePrice,
    });

    return {
      ...symbol,
      breakPoint,
      breakPointPercentage,
      status,
      blackScholes,
      delta,
      leverage,
    };
  }

  private toCollection(
    arr: OptionContractChainDetailsWithPrices[]
  ): TCalcSymbolPrices {
    return arr.reduce((group, item) => {
      // TODO: Replace numeric extraction from instrumentName with a proper shared instrument identifier
      // Call and put contracts do not have a shared unique identifier.
      // Temporary solution uses numeric part of instrumentName as grouping key.
      const key = item.instrumentName.match(/\d+/)?.[0] || '';

      const isBuyContract = item.contractType === POSITIONS_SIDE.buy;
      if (isBuyContract) {
        (group[key] ||= {})['call'] = item;
      } else {
        (group[key] ||= {})['put'] = item;
      }

      (group[key] ||= {})['strikePrice'] = item.strikePrice;

      return group;
    }, {});
  }
  calc() {
    const calls: OptionContractChainDetailsWithPrices[] = [];
    const puts: OptionContractChainDetailsWithPrices[] = [];
    const strikePrices: { strikePrice: number }[] = [];

    const arr = this.#instance.contractChainDetails?.map((symbol) =>
      this.calcPerSymbol(symbol)
    );

    const list = Object.values(this.toCollection(arr || [])).sort(
      (a, b) => a.strikePrice - b.strikePrice
    );

    for (const item of list) {
      switch (this._contractType) {
        case 1:
          {
            if (item.call && item.strikePrice) {
              calls.push(item.call);
              strikePrices.push({ strikePrice: item.strikePrice });
            }
          }
          break;

        case 2:
          {
            if (item.put && item.strikePrice) {
              puts.push(item.put);
              strikePrices.push({ strikePrice: item.strikePrice });
            }
          }
          break;

        default:
          {
            if (item.call && item.put && item.strikePrice) {
              calls.push(item.call);
              puts.push(item.put);
              strikePrices.push({ strikePrice: item.strikePrice });
            }
          }
          break;
      }
    }

    return { ...this.#instance, calls, puts, strikePrices };
  }
}

export {
  calcSymbolCallBreakPointPrice,
  calcSymbolBreakPointPercent,
  calcSymbolPutBreakPointPrice,
  calcSymbolCallStatus,
  calcSymbolPutStatus,
  calcSymbolCallLeverage,
  calcSymbolPutLeverage,
  calcSymbolPutPrices,
  calcSymbolCallPrices,
  CalcSymbolValues,
  type OptionContractChainDetailsWithPrices,
};
